import { Button } from '@/components/ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import type { WorkspaceLocation } from 'honeygrid-types'
import { MapPin, MoreHorizontal, Star, Plus } from 'lucide-react'

interface LocationsTableProps {
	locations: WorkspaceLocation[]
	onSelectLocation: (location: WorkspaceLocation) => void
	onAddLocation: () => void
	selectedLocationId?: string
}

export function LocationsTable({ locations, onSelectLocation, onAddLocation, selectedLocationId }: LocationsTableProps) {
	return (
		<Table>
			<TableHeader className="border-t border-t-[#3A2E2F]/20 border-b border-b-[#3A2E2F] [&_tr]:!border-0">
				<TableRow>
					<TableHead>Name</TableHead>
					<TableHead>Address</TableHead>
					<TableHead>Rating</TableHead>
					<TableHead>Phone</TableHead>
					<TableHead className="w-[50px]"></TableHead>
				</TableRow>
			</TableHeader>
			<TableBody className="[&_tr:last-child]:!border-b [&_tr]:!border-0">
				{locations.length === 0 ? (
					<TableRow className="!border-b !border-b-[#3A2E2F]/20">
						<TableCell colSpan={5} className="h-32 p-0">
							<div className="flex flex-col items-center justify-center text-center space-y-3 bg-[#3A2E2F]/5 h-full py-8">
								<MapPin className="h-8 w-8 text-foreground/60" />
								<p className="text-sm font-medium text-muted-foreground">No locations yet</p>
								<Button variant="outline" size="sm" onClick={onAddLocation}>
									<Plus className="h-4 w-4 mr-2" />
									Add Location
								</Button>
							</div>
						</TableCell>
					</TableRow>
				) : (
					locations.map((location) => (
						<TableRow
							key={location.uuid}
							className={`group cursor-pointer hover:bg-muted/50 !border-b !border-b-[#3A2E2F]/20 ${
								selectedLocationId === location.uuid ? 'bg-muted' : ''
							}`}
							onClick={() => onSelectLocation(location)}
						>
						<TableCell>
							<div className="flex items-center gap-3">
								<div className="flex h-8 w-8 items-center justify-center rounded-lg bg-muted">
									<MapPin className="h-4 w-4" />
								</div>
								<div>
									<div className="font-medium">{location.name}</div>
									<div className="text-sm text-muted-foreground">
										{Array.isArray(location.types) && location.types.length > 0
											? location.types[0]
											: 'Business'}
									</div>
								</div>
							</div>
						</TableCell>
						<TableCell>
							<div className="max-w-[200px] truncate">{location.address}</div>
						</TableCell>
						<TableCell>
							<div className="flex items-center gap-1">
								<Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
								<span className="text-sm">{location.rating}</span>
								<span className="text-xs text-muted-foreground">
									({location.ratings_total})
								</span>
							</div>
						</TableCell>
						<TableCell>
							<span className="text-sm">{location.phone_number || 'N/A'}</span>
						</TableCell>
						<TableCell>
							<Button 
								variant="ghost" 
								size="icon" 
								className="opacity-0 group-hover:opacity-100 transition-opacity"
								onClick={(e) => {
									e.stopPropagation()
									onSelectLocation(location)
								}}
							>
								<MoreHorizontal className="h-4 w-4" />
							</Button>
						</TableCell>
					</TableRow>
				))
					)}
				</TableBody>
			</Table>
	)
}
